import { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import DeleteModal from "../../../components/DeleteModal";
import {
  useCreatePackage,
  useDeletePackage,
  useGetPackages,
  useUpdatePackage,
} from "../../../../api/fleet-management/packages/packages.hooks";
import { MdDelete, MdEdit } from "react-icons/md";

const initialFormState = {
  description: "",
  amount: "",
};

const normalizePackages = (data) => {
  if (Array.isArray(data)) return data;
  return data?.packages || data?.items || [];
};

const mapPackageToForm = (pkg) => ({
  description: pkg?.description || "",
  amount: pkg?.amount !== undefined && pkg?.amount !== null ? String(pkg.amount) : "",
});

const VehiclePackagesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { vehicleId } = useParams();

  const vehicleName = location?.state?.vehicleName || "Vehicle";

  const [form, setForm] = useState(initialFormState);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const {
    data,
    isLoading,
    isError,
    error,
  } = useGetPackages({ vehicleId });

  const { mutate: createPackage, isLoading: isCreating } = useCreatePackage();
  const { mutate: updatePackage, isLoading: isUpdating } = useUpdatePackage();
  const { mutate: deletePackage, isLoading: isDeleting } = useDeletePackage();

  const packages = useMemo(() => normalizePackages(data), [data]);

  const isSubmitting = isCreating || isUpdating;

  const resetFormState = () => {
    setForm(initialFormState);
    setEditingPackage(null);
    setFieldErrors({});
  };

  const openCreateModal = () => {
    resetFormState();
    setFeedback({ type: "", message: "" });
    setShowFormModal(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setForm(mapPackageToForm(pkg));
    setFieldErrors({});
    setFeedback({ type: "", message: "" });
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (isSubmitting) return;
    setShowFormModal(false);
    resetFormState();
  };

  const openDeleteModal = (pkg) => {
    setSelectedPackage(pkg);
    setShowDeleteModal(true);
    setFeedback({ type: "", message: "" });
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedPackage(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.description.trim()) {
      errors.description = "Package description is required.";
    }

    if (!form.amount || Number(form.amount) <= 0) {
      errors.amount = "Package amount must be greater than 0.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!validateForm()) return;

    const payload = {
      vehicleId,
      description: form.description.trim(),
      amount: Number(form.amount),
    };

    if (editingPackage?._id) {
      updatePackage(
        { packageId: editingPackage._id, data: payload },
        {
          onSuccess: () => {
            setFeedback({ type: "success", message: "Package updated successfully." });
            closeFormModal();
          },
          onError: (updateError) => {
            setFeedback({
              type: "danger",
              message: updateError?.response?.data?.message || "Failed to update package.",
            });
          },
        }
      );
      return;
    }

    createPackage(payload, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Package created successfully." });
        closeFormModal();
      },
      onError: (createError) => {
        setFeedback({
          type: "danger",
          message: createError?.response?.data?.message || "Failed to create package.",
        });
      },
    });
  };

  const handleDelete = () => {
    if (!selectedPackage?._id) return;

    setFeedback({ type: "", message: "" });
    deletePackage(
      { packageId: selectedPackage._id },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Package deleted successfully." });
          closeDeleteModal();
        },
        onError: (deleteError) => {
          setFeedback({
            type: "danger",
            message: deleteError?.response?.data?.message || "Failed to delete package.",
          });
        },
      }
    );
  };

  if (!vehicleId) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              Vehicle id is missing in route.
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load packages. {error?.response?.data?.message || "Please try again."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/vehicles")}
              aria-label="Back to vehicles list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <div>
              <h4 className="card-title mb-0">Vehicle Packages</h4>
              <small className="text-muted">{vehicleName}</small>
            </div>
          </div>
          <Button onClick={openCreateModal}>Add Package</Button>
        </div>

        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {packages.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No packages found for this vehicle.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg, index) => (
                    <tr key={pkg?._id || `${pkg?.description || "package"}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{pkg?.description || "-"}</td>
                      <td>{pkg?.amount ?? "-"}</td>
                      <td className="text-center">
                        <div className="d-inline-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openEditModal(pkg)}
                            disabled={!pkg?._id || isSubmitting || isDeleting}
                          >
                            <MdEdit size={18} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDeleteModal(pkg)}
                            disabled={!pkg?._id || isSubmitting || isDeleting}
                          >
                            <MdDelete size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingPackage ? "Edit Package" : "Add Package"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3" controlId="package-description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                isInvalid={Boolean(fieldErrors.description)}
                placeholder="Enter package description"
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.description}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="package-amount">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
                isInvalid={Boolean(fieldErrors.amount)}
                placeholder="Enter package amount"
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">{fieldErrors.amount}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeFormModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Processing...
                </>
              ) : editingPackage ? (
                "Update Package"
              ) : (
                "Create Package"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Package"
        message={`Are you sure you want to delete ${selectedPackage?.description || "this package"}? This action cannot be undone.`}
        confirmText="Delete Package"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default VehiclePackagesPage;
